import React, { useState, useEffect } from "react";
import { Modal, Form, Input, message, Select, Radio, DatePicker, Switch, Row, Col, Tag } from "antd";
import { UploadOutlined, MobileOutlined, DesktopOutlined, GlobalOutlined, InfoCircleOutlined } from "@ant-design/icons";
import BannerServ from "../service/BannerServ";
import ProductServ from "../service/ProductServ";
import CategoryServ from "../service/CategoryServ";

import SafeImage, { getImageUrl } from "./SafeImage";
import UploadImageUploader from "./UploadImageUploader";
import dayjs from "dayjs";
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../util/canvasUtils';

const { Option } = Select;
const { RangePicker } = DatePicker;

// Helper function to resolve placement guidance, crop ratio, and resolution specs
export const getBannerPlacementMeta = (targetPlatform = 'all', position = 'DEFAULT', screens = ['home']) => {
    const isMobile = targetPlatform === 'mobile';
    const isWeb = targetPlatform === 'web';
    
    // Web Home Right
    if (isWeb && position === 'HOME_RIGHT') {
        return {
            title: "Web App — Hero Right Side Banner",
            screenLabel: "Home Screen (Right Split Hero)",
            dimensions: "1200 × 600 px",
            aspectRatio: 2,
            aspectLabel: "2:1 Aspect Ratio",
            hint: "Displays on the right side of the hero section on desktop website",
            icon: "🖥️",
            bgGradient: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
            borderColor: "#bfdbfe"
        };
    }

    // Web Home Top Slider
    if (isWeb && (position === 'DEFAULT' || position === 'HOME_TOP_SLIDER')) {
        return {
            title: "Web App — Main Top Slider",
            screenLabel: "Home Screen (Top Full Width Carousel)",
            dimensions: "1800 × 600 px",
            aspectRatio: 3,
            aspectLabel: "3:1 Aspect Ratio",
            hint: "Displays as the full-width main top slider on desktop website",
            icon: "🖥️",
            bgGradient: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
            borderColor: "#bbf7d0"
        };
    }

    // Mobile Top Hero Carousel
    if (isMobile && (position === 'DEFAULT' || position === 'HOME_TOP_CAROUSEL')) {
        return {
            title: "Mobile App — Top Hero Carousel",
            screenLabel: "Home Screen (Top Mobile Carousel)",
            dimensions: "1200 × 600 px",
            aspectRatio: 2,
            aspectLabel: "2:1 Aspect Ratio",
            hint: "Displays at the top hero section of the mobile smartphone app",
            icon: "📱",
            bgGradient: "linear-gradient(135deg, #fefce8 0%, #fef08a 100%)",
            borderColor: "#fde047"
        };
    }

    // Home Mid In-Feed Banner
    if (position === 'HOME_MID_SECTION') {
        return {
            title: isMobile ? "Mobile App — Mid In-Feed Banner" : "Web App — Mid In-Feed Banner",
            screenLabel: "Home Screen (Middle Section)",
            dimensions: isWeb ? "1500 × 500 px" : "1200 × 400 px",
            aspectRatio: 3,
            aspectLabel: "3:1 Aspect Ratio",
            hint: "Displays in-between shop list or product sections",
            icon: isMobile ? "📱" : "🖥️",
            bgGradient: "linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)",
            borderColor: "#e9d5ff"
        };
    }

    // Category Screen Header Banner
    if (screens.includes('category') || position === 'CATEGORY_TOP') {
        return {
            title: "Category Screen Header Banner",
            screenLabel: "Category Page Header",
            dimensions: isWeb ? "1500 × 500 px" : "1200 × 400 px",
            aspectRatio: 3,
            aspectLabel: "3:1 Aspect Ratio",
            hint: "Displays at the top of category listing screen",
            icon: "🏷️",
            bgGradient: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)",
            borderColor: "#fed7aa"
        };
    }

    // Cart Screen Promo Banner
    if (screens.includes('cart') || position === 'CART_PROMO') {
        return {
            title: "Cart Screen Deal Banner",
            screenLabel: "Cart Page (Top Promo)",
            dimensions: "1200 × 400 px",
            aspectRatio: 3,
            aspectLabel: "3:1 Aspect Ratio",
            hint: "Displays at top of cart page for special deals",
            icon: "🛒",
            bgGradient: "linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)",
            borderColor: "#fbcfe8"
        };
    }

    // Checkout Screen Promo Banner
    if (screens.includes('checkout') || position === 'CHECKOUT_PROMO') {
        return {
            title: "Checkout Screen Offer Banner",
            screenLabel: "Checkout Page (Payment Offer)",
            dimensions: "1200 × 400 px",
            aspectRatio: 3,
            aspectLabel: "3:1 Aspect Ratio",
            hint: "Displays discount or bank offers on checkout screen",
            icon: "💳",
            bgGradient: "linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)",
            borderColor: "#a5f3fc"
        };
    }

    // Fallback Cross-Platform Standard
    return {
        title: "Cross-Platform Standard Banner",
        screenLabel: "Home / General Screen",
        dimensions: "1200 × 600 px",
        aspectRatio: 2,
        aspectLabel: "2:1 Aspect Ratio",
        hint: "Cross-platform banner compatible with Web & Mobile App",
        icon: "🌐",
        bgGradient: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
        borderColor: "#cbd5e1"
    };
};

// ─── Centered Calendar Modal Picker ───────────────────────────────────────────
const DateRangeModalPicker = ({ value, onChange }) => {
    const [open, setOpen] = useState(false);
    const [tempDates, setTempDates] = useState(null);

    const displayValue = value || tempDates;
    const startLabel = displayValue?.[0] ? displayValue[0].format('YYYY-MM-DD') : '';
    const endLabel   = displayValue?.[1] ? displayValue[1].format('YYYY-MM-DD') : '';

    const handleOpen = () => {
        setTempDates(value || null);
        setOpen(true);
    };

    const handleConfirm = () => {
        if (tempDates?.[0] && tempDates?.[1]) onChange(tempDates);
        setOpen(false);
    };

    return (
        <>
            {/* Trigger Button — looks like Ant DatePicker input */}
            <div
                onClick={handleOpen}
                style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                    padding: '5px 10px', border: '1px solid #d9d9d9', borderRadius: 6,
                    cursor: 'pointer', background: '#fff', fontSize: 13, minHeight: 32,
                    color: startLabel ? '#1e293b' : '#bfbfbf', userSelect: 'none',
                }}
            >
                <span style={{ fontSize: 14, color: '#94a3b8' }}>📅</span>
                <span style={{ flex: 1 }}>{startLabel || 'Start date'}</span>
                <span style={{ color: '#c0c0c0', fontSize: 12 }}>→</span>
                <span style={{ flex: 1, textAlign: 'right' }}>{endLabel || 'End date'}</span>
            </div>

            {/* Centered Calendar Modal */}
            <Modal
                open={open}
                onCancel={() => setOpen(false)}
                footer={null}
                centered
                closable={false}
                width={570}
                style={{ borderRadius: 16 }}
                styles={{
                    content: { padding: 0, borderRadius: 16, overflow: 'hidden', boxShadow: '0 24px 60px rgba(15,23,42,0.22)' },
                    body: { padding: 0 },
                    mask: { backdropFilter: 'blur(5px)', background: 'rgba(15,23,42,0.5)' },
                }}
                destroyOnClose
            >
                <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden' }}>
                    {/* Header */}
                    <div style={{
                        padding: '14px 20px',
                        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                        <div>
                            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                                📅 Select Active Schedule
                            </h3>
                            <p style={{ margin: '2px 0 0', fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>
                                Pick a start and end date for the banner
                            </p>
                        </div>
                        <button onClick={() => setOpen(false)} style={{
                            background: 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer',
                            color: '#fff', fontSize: 14, width: 26, height: 26, borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>✕</button>
                    </div>

                    {/* Inline RangePicker Panel */}
                    <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'center' }}>
                        <style>{`
                            .nz-modal-rangepicker .ant-picker { opacity: 0; height: 0; width: 0; position: absolute; pointer-events: none; }
                            .nz-modal-rangepicker .ant-picker-dropdown { position: static !important; transform: none !important; z-index: 1 !important; }
                            .nz-modal-rangepicker .ant-picker-dropdown .ant-picker-panel-container { box-shadow: none !important; border: 1px solid #e2e8f0 !important; border-radius: 10px !important; }
                        `}</style>
                        <div className="nz-modal-rangepicker" style={{ position: 'relative', minHeight: 320 }}>
                            <RangePicker
                                open
                                value={tempDates}
                                onChange={(dates) => setTempDates(dates)}
                                getPopupContainer={(trigger) => trigger.parentElement}
                                popupStyle={{ position: 'static' }}
                            />
                        </div>
                    </div>

                    {/* Footer */}
                    <div style={{
                        padding: '12px 20px', background: '#f8fafc', borderTop: '1px solid #e2e8f0',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                        <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                            {tempDates?.[0] && tempDates?.[1]
                                ? `${tempDates[0].format('MMM D, YYYY')}  →  ${tempDates[1].format('MMM D, YYYY')}`
                                : 'No dates selected'}
                        </span>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => setOpen(false)} type="button" style={{
                                padding: '6px 16px', background: '#fff', border: '1px solid #cbd5e1',
                                borderRadius: 6, fontSize: 13, fontWeight: 600, color: '#475569', cursor: 'pointer',
                            }}>Cancel</button>
                            <button
                                onClick={handleConfirm}
                                type="button"
                                disabled={!tempDates?.[0] || !tempDates?.[1]}
                                style={{
                                    padding: '6px 20px',
                                    background: 'linear-gradient(135deg, #4776E6 0%, #8E54E9 100%)',
                                    border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700,
                                    color: '#fff', cursor: 'pointer',
                                    opacity: (!tempDates?.[0] || !tempDates?.[1]) ? 0.5 : 1,
                                    boxShadow: '0 2px 8px rgba(71,118,230,0.3)',
                                }}
                            >Confirm Dates</button>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};
// ──────────────────────────────────────────────────────────────────────────────

const AddBanner = ({ open, refreshBanners, bannerDetails, isEditing }) => {
    const bannerId = bannerDetails?._id || bannerDetails?.id;
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);

    const [imagePreview, setImagePreview] = useState("");
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);

    const [imageSource, setImageSource] = useState('upload');

    // Crop States
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [isCropModalOpen, setIsCropModalOpen] = useState(false);
    const [imageToCrop, setImageToCrop] = useState(null);

    useEffect(() => {
        fetchSupportData();
    }, []);

    const fetchSupportData = async () => {
        try {
            const [prodRes, catRes] = await Promise.all([
                ProductServ.index({ limit: 1000 }),
                CategoryServ.index({ limit: 1000 })
            ]);
            setProducts(prodRes?.data || []);
            setCategories(catRes?.data || []);
        } catch (error) {
            console.error("Error fetching support data:", error);
        }
    };

    // Load form data on edit
    useEffect(() => {
        if (isEditing && bannerDetails) {
            form.setFieldsValue({
                title: bannerDetails.title,
                imageUrl: bannerDetails.imageUrl,
                targetPlatform: bannerDetails.targetPlatform || 'all',
                type: bannerDetails.type || 'global',
                category: bannerDetails.category?._id || bannerDetails.category,
                redirectType: bannerDetails.redirectType || 'none',
                redirectId: bannerDetails.redirectId,
                screens: bannerDetails.screens || ['home'],
                position: bannerDetails.position || 'DEFAULT',
                priority: bannerDetails.priority || 0,
                isActive: bannerDetails.isActive ?? true,
                isLive: bannerDetails.isLive ?? true,
                dates: [
                    bannerDetails.startDate ? dayjs(bannerDetails.startDate) : dayjs(),
                    bannerDetails.endDate ? dayjs(bannerDetails.endDate) : dayjs().add(1, 'year')
                ]
            });
            setImagePreview(bannerDetails.imageUrl || "");
        } else {
            form.resetFields();
            form.setFieldsValue({
                targetPlatform: 'all',
                isActive: true,
                isLive: true,
                screens: ['home'],
                type: 'global',
                position: 'DEFAULT',
                redirectType: 'none',
                priority: 0,
                dates: [dayjs(), dayjs().add(1, 'year')]
            });
            setImagePreview("");
            setImageSource('upload');
        }
    }, [isEditing, bannerDetails, form]);

    const onCropComplete = (croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    };

    const handleCropSave = async () => {
        try {
            const { file } = await getCroppedImg(imageToCrop, croppedAreaPixels);
            setIsCropModalOpen(false);
            await uploadCroppedImage(file);
        } catch (e) {
            message.error("Crop failed: " + e.message);
        }
    };

    const uploadCroppedImage = async (file) => {
        const formData = new FormData();
        formData.append("image", file, "cropped-banner.jpg");

        const key = "upload-image";

        try {
            message.loading({
                content: "Uploading image...",
                key,
                duration: 0,
            });

            const res = await BannerServ.uploadImage(formData);

            let uploadedUrl = null;

            if (typeof res === "string") {
                uploadedUrl = res;
            } else if (res) {
                uploadedUrl =
                    res.url ||
                    res.imageUrl ||
                    res.data?.url ||
                    res.data?.imageUrl;
            }

            if (!uploadedUrl) {
                message.error({
                    content: "Upload successful, but no URL returned.",
                    key,
                });
                return;
            }

            const fullUrl = getImageUrl(uploadedUrl);

            setImagePreview(fullUrl);

            form.setFieldsValue({
                imageUrl: fullUrl,
            });

            message.success({
                content: "Image uploaded successfully!",
                key,
                duration: 2,
            });

        } catch (error) {
            const apiError =
                error.response?.data?.message ||
                error.message ||
                "Unknown error";

            message.error({
                content: "Upload failed: " + apiError,
                key,
                duration: 3,
            });
        }
    };

    const handleSubmit = async (values) => {
        try {
            setSubmitting(true);
            const payload = {
                ...values,
                startDate: values.dates[0].toDate(),
                endDate: values.dates[1].toDate(),
            };
            delete payload.dates;

            if (isEditing && bannerId) {
                await BannerServ.update(bannerId, payload);
                message.success("Banner updated successfully");
            } else {
                await BannerServ.create(payload);
                message.success("Banner created successfully");
            }
            refreshBanners(true);
        } catch (error) {
            console.error("Error saving banner:", error);
            message.error(error.message || "Failed to save banner");
        } finally {
            setSubmitting(false);
        }
    };

    const handleSave = () => {
        form.validateFields().then(values => {
            handleSubmit(values);
        }).catch(errorInfo => {
            if (errorInfo?.errorFields?.some(field => field.name.includes('imageUrl'))) {
                message.error("Please upload a banner image");
            } else {
                message.error("Please fill all required fields");
            }
        });
    };

    const labelStyle = { fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: 4 };

    // Watch fields for live placement specs
    const targetPlatform = Form.useWatch('targetPlatform', form) || 'all';
    const position = Form.useWatch('position', form) || 'DEFAULT';
    const screens = Form.useWatch('screens', form) || ['home'];

    const currentMeta = getBannerPlacementMeta(targetPlatform, position, screens);

    // Dynamic placement section choices based on target platform
    const handlePlacementChange = (posKey) => {
        let targetScreens = ['home'];
        let posVal = posKey;

        if (posKey === 'CATEGORY_TOP') {
            targetScreens = ['category'];
            form.setFieldsValue({ type: 'category' });
        } else if (posKey === 'CART_PROMO') {
            targetScreens = ['cart'];
            form.setFieldsValue({ type: 'global' });
        } else if (posKey === 'CHECKOUT_PROMO') {
            targetScreens = ['checkout'];
            form.setFieldsValue({ type: 'global' });
        } else {
            form.setFieldsValue({ type: 'global' });
        }

        form.setFieldsValue({
            position: posVal,
            screens: targetScreens
        });
    };

    return (
        <Modal
            open={open}
            onCancel={() => refreshBanners(false)}
            footer={null}
            width={760}
            centered
            closable={false}
            className="ss-modal"
            bodyStyle={{ padding: 0 }}
            maskStyle={{ backdropFilter: 'blur(3px)' }}
            style={{ borderRadius: 14, overflow: 'hidden' }}
            styles={{ content: { padding: 0, borderRadius: 14, overflow: 'hidden' }, body: { padding: 0 } }}
        >
            <style>{`
                .ss-modal .ant-form-item-explain-error {
                    font-size: 10px !important;
                    font-weight: 700 !important;
                    margin-top: 2px !important;
                    color: #ef4444 !important;
                }
                .ss-modal .ant-form-item-has-error .ant-input,
                .ss-modal .ant-form-item-has-error .ant-select-selector {
                    border-color: #fecaca !important;
                    background: #fffafa !important;
                }
                .platform-radio-btn .ant-radio-button-wrapper-checked {
                    background: linear-gradient(135deg, #4776E6 0%, #8E54E9 100%) !important;
                    color: #fff !important;
                    border-color: transparent !important;
                    font-weight: 700 !important;
                }
                .nz-clean-calendar-popup {
                    z-index: 99999 !important;
                    box-shadow: 0 16px 40px rgba(15, 23, 42, 0.25) !important;
                    border-radius: 14px !important;
                    border: 1px solid #cbd5e1 !important;
                    background: #ffffff !important;
                }
                .nz-clean-calendar-popup .ant-picker-panel-container {
                    border-radius: 14px !important;
                    overflow: hidden !important;
                    box-shadow: none !important;
                }
            `}</style>
            <div style={{ background: '#fff', borderRadius: 14, overflow: 'hidden' }}>
                {/* Header */}
                <div style={{
                    padding: '14px 20px',
                    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    color: '#fff'
                }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                            {isEditing ? "✏️ Edit Banner" : "✨ Create New Banner"}
                        </h2>
                        <p style={{ margin: '2px 0 0', fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>
                            Configure target platform, screen placement, and upload cropped image
                        </p>
                    </div>
                    <button
                        onClick={() => refreshBanners(false)}
                        style={{ background: 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer', color: '#fff', fontSize: 14, width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        ✕
                    </button>
                </div>

                <div style={{ padding: '16px 20px', maxHeight: '78vh', overflowY: 'auto' }}>
                    <Form
                        form={form}
                        layout="vertical"
                        size="small"
                        requiredMark={false}
                        onFinish={handleSubmit}
                        onValuesChange={(changedValues) => {
                            if (changedValues.imageUrl !== undefined) {
                                setImagePreview(changedValues.imageUrl);
                            }
                        }}
                    >
                        {/* STEP 1: TARGET PLATFORM SELECTOR */}
                        <div style={{
                            padding: '12px 14px',
                            background: '#f8fafc',
                            borderRadius: 10,
                            border: '1px solid #e2e8f0',
                            marginBottom: 16
                        }}>
                            <span style={{ ...labelStyle, color: '#475569', fontSize: 11, marginBottom: 8 }}>
                                STEP 1: Select Target Platform <span style={{ color: '#ef4444' }}>*</span>
                            </span>
                            <Form.Item name="targetPlatform" noStyle>
                                <Radio.Group
                                    buttonStyle="solid"
                                    style={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}
                                    onChange={(e) => {
                                        const plat = e.target.value;
                                        if (plat === 'web') form.setFieldsValue({ position: 'HOME_TOP_SLIDER', screens: ['home'] });
                                        else if (plat === 'mobile') form.setFieldsValue({ position: 'HOME_TOP_CAROUSEL', screens: ['home'] });
                                        else form.setFieldsValue({ position: 'DEFAULT', screens: ['home'] });
                                    }}
                                >
                                    <Radio.Button value="all" style={{ borderRadius: 6, textAlign: 'center', height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 12 }}>
                                        <GlobalOutlined /> Both (Web & Mobile)
                                    </Radio.Button>
                                    <Radio.Button value="mobile" style={{ borderRadius: 6, textAlign: 'center', height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 12 }}>
                                        <MobileOutlined /> Mobile App Only
                                    </Radio.Button>
                                    <Radio.Button value="web" style={{ borderRadius: 6, textAlign: 'center', height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 12 }}>
                                        <DesktopOutlined /> Web App Only
                                    </Radio.Button>
                                </Radio.Group>
                            </Form.Item>
                        </div>

                        <Row gutter={20}>
                            {/* LEFT COLUMN */}
                            <Col xs={24} md={12}>
                                <Form.Item name="title" label={<span style={labelStyle}>Banner Title</span>} style={{ marginBottom: 12 }}>
                                    <Input placeholder="e.g. Summer Festival Offers 2026" style={{ borderRadius: 6 }} />
                                </Form.Item>

                                {/* STEP 2: SCREEN & PLACEMENT SELECTION */}
                                <Form.Item label={<span style={labelStyle}>STEP 2: Target Placement / Section <span style={{ color: '#ef4444' }}>*</span></span>} style={{ marginBottom: 12 }}>
                                    <Select
                                        value={position}
                                        onChange={handlePlacementChange}
                                        style={{ width: '100%', borderRadius: 6 }}
                                    >
                                        {targetPlatform === 'web' ? (
                                            <>
                                                <Option value="HOME_TOP_SLIDER">🖥️ Home Screen — Main Top Slider (3:1)</Option>
                                                <Option value="HOME_RIGHT">🖥️ Home Screen — Hero Right Section (2:1)</Option>
                                                <Option value="HOME_MID_SECTION">🖥️ Home Screen — Mid In-Feed Section (3:1)</Option>
                                                <Option value="CATEGORY_TOP">🏷️ Category Screen — Header Banner (3:1)</Option>
                                                <Option value="CART_PROMO">🛒 Cart Screen — Deal Banner (3:1)</Option>
                                                <Option value="CHECKOUT_PROMO">💳 Checkout Screen — Offer Banner (3:1)</Option>
                                            </>
                                        ) : targetPlatform === 'mobile' ? (
                                            <>
                                                <Option value="HOME_TOP_CAROUSEL">📱 Home Screen — Top Hero Carousel (2:1)</Option>
                                                <Option value="HOME_MID_SECTION">📱 Home Screen — Mid In-Feed Banner (3:1)</Option>
                                                <Option value="CATEGORY_TOP">🏷️ Category Screen — Header Banner (3:1)</Option>
                                                <Option value="CART_PROMO">🛒 Cart Screen — Deal Banner (3:1)</Option>
                                                <Option value="CHECKOUT_PROMO">💳 Checkout Screen — Payment Offer (3:1)</Option>
                                            </>
                                        ) : (
                                            <>
                                                <Option value="DEFAULT">🌐 Home Screen — Top Carousel (2:1)</Option>
                                                <Option value="HOME_MID_SECTION">🌐 Home Screen — Mid In-Feed Banner (3:1)</Option>
                                                <Option value="CATEGORY_TOP">🏷️ Category Screen — Header Banner (3:1)</Option>
                                                <Option value="CART_PROMO">🌐 Cart Screen — Deal Banner (3:1)</Option>
                                                <Option value="CHECKOUT_PROMO">🌐 Checkout Screen — Offer Banner (3:1)</Option>
                                            </>
                                        )}
                                    </Select>
                                </Form.Item>

                                <Form.Item name="screens" hidden><Select mode="multiple" /></Form.Item>
                                <Form.Item name="position" hidden><Input /></Form.Item>

                                <Form.Item name="redirectType" label={<span style={labelStyle}>Redirection Type</span>} style={{ marginBottom: 12 }}>
                                    <Select style={{ borderRadius: 6 }} onChange={() => form.setFieldsValue({ redirectId: null })}>
                                        <Option value="none">No Redirect</Option>
                                        <Option value="product">Product Page</Option>
                                        <Option value="category">Category Page</Option>
                                        <Option value="external">External Link URL</Option>
                                    </Select>
                                </Form.Item>

                                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.redirectType !== curr.redirectType}>
                                    {({ getFieldValue }) => {
                                        const type = getFieldValue('redirectType');
                                        if (type === 'none') return null;
                                        return (
                                            <Form.Item name="redirectId" label={<span style={labelStyle}>{type === 'external' ? 'URL' : 'Target Item'} <span style={{ color: '#ef4444' }}>*</span></span>} rules={[{ required: true, message: 'Target is required' }]} style={{ marginBottom: 12 }}>
                                                {type === 'product' ? (
                                                    <Select style={{ borderRadius: 6 }} showSearch placeholder="Select target product" filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                                                        {products.map(p => <Option key={p._id} value={p._id}>{p.name}</Option>)}
                                                    </Select>
                                                ) : type === 'category' ? (
                                                    <Select style={{ borderRadius: 6 }} showSearch placeholder="Select target category" filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                                                        {categories.map(c => <Option key={c._id} value={c._id}>{c.name}</Option>)}
                                                    </Select>
                                                ) : (
                                                    <Input style={{ borderRadius: 6 }} placeholder="https://nearzon.com/promo" />
                                                )}
                                            </Form.Item>
                                        );
                                    }}
                                </Form.Item>

                                <Form.Item name="dates" label={<span style={labelStyle}>Active Schedule <span style={{ color: '#ef4444' }}>*</span></span>} rules={[{ required: true, message: 'Schedule is required' }]} style={{ marginBottom: 12 }}>
                                    <DateRangeModalPicker />
                                </Form.Item>

                                <Row gutter={12}>
                                    <Col span={8}>
                                        <Form.Item name="priority" label={<span style={labelStyle}>Priority</span>} style={{ marginBottom: 12 }}>
                                            <Input type="number" min={0} placeholder="0" style={{ borderRadius: 6 }} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item name="isActive" label={<span style={labelStyle}>Status</span>} valuePropName="checked" style={{ marginBottom: 12 }}>
                                            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item name="isLive" label={<span style={labelStyle}>Visibility</span>} valuePropName="checked" style={{ marginBottom: 12 }}>
                                            <Switch checkedChildren="Live" unCheckedChildren="Draft" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Col>

                            {/* RIGHT COLUMN: VISUAL SPECIFICATION & IMAGE UPLOADER */}
                            <Col xs={24} md={12}>
                                {/* LIVE SPECIFICATIONS & GUIDANCE CARD */}
                                <div style={{
                                    padding: '12px 14px',
                                    background: currentMeta.bgGradient,
                                    border: `1px solid ${currentMeta.borderColor}`,
                                    borderRadius: 10,
                                    marginBottom: 16
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                        <span style={{ fontSize: 12, fontWeight: 800, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>
                                            {currentMeta.icon} {currentMeta.title}
                                        </span>
                                        <Tag color="blue" style={{ margin: 0, fontWeight: 700, borderRadius: 4 }}>
                                            {currentMeta.aspectLabel}
                                        </Tag>
                                    </div>
                                    
                                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8 }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>Recommended Size</div>
                                            <div style={{ fontSize: 15, fontWeight: 900, color: '#0f172a' }}>{currentMeta.dimensions}</div>
                                        </div>
                                        <div style={{ flex: 1.2 }}>
                                            <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>Placement Area</div>
                                            <div style={{ fontSize: 11, fontWeight: 600, color: '#334155' }}>{currentMeta.screenLabel}</div>
                                        </div>
                                    </div>

                                    <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px stroke rgba(0,0,0,0.06)', fontSize: 10, color: '#64748b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <InfoCircleOutlined style={{ color: '#3b82f6' }} /> {currentMeta.hint}
                                    </div>
                                </div>

                                <Form.Item label={<span style={labelStyle}>Banner Image Upload <span style={{ color: '#ef4444' }}>*</span></span>} style={{ marginBottom: 12 }}>
                                    <Radio.Group value={imageSource} onChange={e => setImageSource(e.target.value)} style={{ marginBottom: 10 }}>
                                        <Radio value="upload" style={{ fontSize: 12 }}>Upload & Crop</Radio>
                                        <Radio value="url" style={{ fontSize: 12 }}>Image Direct URL</Radio>
                                    </Radio.Group>

                                    <div style={{ marginTop: 4 }}>
                                        {imageSource === "upload" ? (
                                            <div style={{ marginBottom: 12 }}>
                                                <UploadImageUploader
                                                    profileType="BANNER"
                                                    value={imagePreview}
                                                    onPreviewImage={(dataUrl, file) => {
                                                        setImageToCrop(dataUrl);
                                                        setIsCropModalOpen(true);
                                                    }}
                                                >
                                                    <div style={{ width: '100%' }}>
                                                        {imagePreview ? (
                                                            <div className="banner-image-preview" style={{
                                                                width: '100%', height: 160, borderRadius: '8px', overflow: 'hidden',
                                                                border: '1px dashed #cbd5e1', position: 'relative', cursor: 'pointer'
                                                            }}>
                                                                <SafeImage src={imagePreview} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                                                <div className="banner-overlay">
                                                                    <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>Click to Re-Crop / Change</span>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="banner-upload-placeholder" style={{
                                                                width: '100%', padding: '20px 16px', border: '2px dashed #93c5fd', borderRadius: '10px',
                                                                background: '#eff6ff', cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s'
                                                            }}>
                                                                <UploadOutlined style={{ fontSize: 28, color: '#3b82f6', marginBottom: 8 }} />
                                                                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>Click to Upload & Crop Banner</div>
                                                                <div style={{ fontSize: 10, color: '#60a5fa', marginTop: 4 }}>Auto locks to {currentMeta.aspectLabel} ({currentMeta.dimensions})</div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </UploadImageUploader>
                                            </div>
                                        ) : (
                                            <div style={{ marginBottom: 12 }}>
                                                <Form.Item rules={[{ required: true, message: 'URL required' }]} noStyle>
                                                    <Input placeholder="https://..." value={imagePreview} style={{ borderRadius: 6 }} onChange={e => { setImagePreview(e.target.value); form.setFieldsValue({ imageUrl: e.target.value }); }} />
                                                </Form.Item>
                                                {imagePreview && <div style={{ marginTop: 8 }}><SafeImage src={imagePreview} style={{ width: '100%', height: 120, objectFit: 'contain', borderRadius: 8, border: '1px solid #e2e8f0' }} /></div>}
                                            </div>
                                        )}
                                    </div>
                                    <Form.Item name="imageUrl" rules={[{ required: true, message: 'Banner image is required' }]} hidden><Input /></Form.Item>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </div>

                {/* Footer Actions */}
                <div className="nz-form-footer" style={{ padding: '12px 20px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    <button onClick={() => refreshBanners(false)} type="button" style={{
                        padding: '7px 18px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', fontWeight: 600, color: '#475569', cursor: 'pointer'
                    }}>
                        Cancel
                    </button>
                    <button onClick={handleSave} type="button" disabled={submitting} style={{
                        padding: '7px 22px', background: 'linear-gradient(135deg, #4776E6 0%, #8E54E9 100%)', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 700, color: '#fff', cursor: 'pointer', boxShadow: '0 2px 6px rgba(71,118,230,0.3)', opacity: submitting ? 0.7 : 1
                    }}>
                        {submitting ? 'Saving Banner...' : 'Save Banner'}
                    </button>
                </div>
            </div>

            {/* CROP MODAL WITH AUTOMATIC ASPECT LOCK */}
            <Modal
                open={isCropModalOpen}
                onCancel={() => setIsCropModalOpen(false)}
                footer={null}
                width={640}
                centered
                closable={false}
                destroyOnClose
                bodyStyle={{ padding: 0 }}
                style={{ borderRadius: 12, overflow: 'hidden' }}
                styles={{ content: { padding: 0, borderRadius: 12, overflow: 'hidden' }, body: { padding: 0 } }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid #f1f5f9', background: '#fff' }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>
                            ✂️ Crop Banner Image ({currentMeta.aspectLabel})
                        </h2>
                        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Target Size: {currentMeta.dimensions}</span>
                    </div>
                    <button
                        onClick={() => setIsCropModalOpen(false)}
                        style={{ background: '#f1f5f9', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: 13, width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        ✕
                    </button>
                </div>
                <div style={{ padding: '20px' }}>
                    <div style={{ position: 'relative', width: '100%', height: 320, background: '#1e293b', borderRadius: 8, overflow: 'hidden' }}>
                        <Cropper
                            image={imageToCrop}
                            crop={crop}
                            zoom={zoom}
                            aspect={currentMeta.aspectRatio}
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                        />
                    </div>
                    <div style={{ marginTop: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>
                            <span>ZOOM ADJUSTMENT</span>
                            <span>{Math.round(zoom * 100)}%</span>
                        </div>
                        <input
                            type="range"
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.1}
                            onChange={(e) => setZoom(e.target.value)}
                            style={{ width: '100%', accentColor: '#4776E6' }}
                        />
                    </div>
                </div>
                <div className="nz-form-footer" style={{ marginTop: 0, padding: '12px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10, background: '#f8fafc' }}>
                    <button onClick={() => setIsCropModalOpen(false)} type="button" style={{
                        padding: '6px 16px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', fontWeight: 600, color: '#475569', cursor: 'pointer'
                    }}>
                        Cancel
                    </button>
                    <button onClick={handleCropSave} type="button" style={{
                        padding: '6px 20px', background: 'linear-gradient(135deg, #4776E6 0%, #8E54E9 100%)', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, color: '#fff', cursor: 'pointer', boxShadow: '0 2px 4px rgba(71,118,230,0.2)'
                    }}>
                        Crop & Save
                    </button>
                </div>
            </Modal>
        </Modal>
    );
};

export default AddBanner;
